import { AfterContentInit, ChangeDetectionStrategy, Component, ContentChild, ElementRef, Input, Renderer2 } from "@angular/core";
import { BooleanInput, coerceBooleanProperty } from "@vattenfall/util";

/**
 * Vattenfall UI `ui-error` is common interface for displaying an error to the user.
 *
 * The `#errorMessage` template reference is used for declaring which element should be displayed to the user if an error has occurred. There can only be one message element.
 *
 * Optionally, you can use a different icons by setting the `icon` property.
 */
@Component({
  selector: "ui-error, [ui-error]",
  templateUrl: "./error.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: "ds-error",
    "[class.ds-is-error]": "error && icon === 'error'",
    "[class.ds-is-warning]": "error && icon === 'warning'",
    "[class.ds-is-waiting]": "error && icon === 'waiting'",
  },
})
export class ErrorComponent implements AfterContentInit {
  /**
   * Whether the component error state is activated
   */
  @Input()
  get error(): boolean {
    return this._error;
  }
  set error(value: BooleanInput) {
    this._error = coerceBooleanProperty(value);
  }
  private _error = true;
  /**
   * Which icon to show in case of an error. Default is `error`.
   */
  @Input()
  public icon: "warning" | "error" | "waiting" = "error";
  /**
   * A user message to display to the client in case of an error
   */
  @ContentChild("errorMessage", { read: ElementRef })
  private errorMessage!: ElementRef<HTMLElement>;

  constructor(private renderer: Renderer2) {}

  ngAfterContentInit() {
    if (this.errorMessage) this.renderer.addClass(this.errorMessage.nativeElement, "ds-error-message");
  }
}
